import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  Box,
  Button,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useNavigate } from 'react-router';
import { useAuth } from '../../hooks/useAuth';
import routes from '../../routes/routes';
import {
  terminalColors,
  terminalToolbarSx,
  terminalBottomBarSx,
  terminalPageShellSx,
  terminalBarToolbarSx,
  terminalBarIconButtonSx,
  terminalExitButtonSx,
} from '../../theme/terminalStyles';

const drawerPaperSx = {
  width: { xs: 'min(300px, 92vw)', sm: 300 },
  maxWidth: '92vw',
  boxSizing: 'border-box',
  background: `linear-gradient(180deg, ${terminalColors.gradientTop} 0%, ${terminalColors.gradientBottom} 100%)`,
};

const TerminalLayout = ({ title, titleIcon, backTo, endActions, bottomBar, children }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { authState, logoutUser } = useAuth();
  const user = authState.user;

  const hasBottomBar = Boolean(bottomBar);

  const handleLogout = () => {
    setMenuOpen(false);
    logoutUser();
    navigate(routes.login);
  };

  return (
    <Box sx={terminalPageShellSx}>
      <AppBar position="static" elevation={0} sx={{ ...terminalToolbarSx, flexShrink: 0 }}>
        <Toolbar sx={terminalBarToolbarSx}>
          {backTo ? (
            <IconButton
              aria-label="назад"
              onClick={() => navigate(backTo)}
              sx={terminalBarIconButtonSx}
            >
              <ArrowBackIcon />
            </IconButton>
          ) : null}
          <Box
            sx={{
              flexGrow: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
            }}
          >
            {titleIcon ? (
              <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, '& svg': { fontSize: 26 } }}>
                {titleIcon}
              </Box>
            ) : null}
            <Typography
              variant="subtitle1"
              noWrap
              sx={{
                fontWeight: 600,
                fontSize: '0.9rem',
                color: terminalColors.textPrimary,
              }}
            >
              {title}
            </Typography>
          </Box>
          {endActions ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>{endActions}</Box>
          ) : null}
          <IconButton aria-label="menu" onClick={() => setMenuOpen(true)} sx={terminalBarIconButtonSx}>
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Drawer
        anchor="left"
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        PaperProps={{ sx: drawerPaperSx }}
      >
        <Box
          role="presentation"
          sx={{
            width: '100%',
            boxSizing: 'border-box',
            p: 2.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              minWidth: 0,
              p: 1.5,
              borderRadius: 2,
              bgcolor: terminalColors.cardWhite,
              boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
            }}
          >
            <AccountCircleIcon
              sx={{ fontSize: 48, color: terminalColors.textPrimary, flexShrink: 0 }}
            />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap title={user?.name}>
                {user?.name || '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap title={user?.login}>
                {user?.login || ''}
              </Typography>
            </Box>
          </Box>
          <Divider />
          <Button
            variant="contained"
            disableElevation
            fullWidth
            size="large"
            onClick={handleLogout}
            sx={{
              ...terminalExitButtonSx,
              py: 1.25,
              fontSize: '1rem',
            }}
          >
            Вийти
          </Button>
        </Box>
      </Drawer>
      <Box
        component="main"
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
          px: { xs: 1.5, sm: 2 },
          py: 1,
          pb: hasBottomBar ? 1 : 2,
          bgcolor: terminalColors.gradientTop,
          boxSizing: 'border-box',
        }}
      >
        {children}
      </Box>
      {hasBottomBar ? <Box sx={terminalBottomBarSx}>{bottomBar}</Box> : null}
    </Box>
  );
};

export default TerminalLayout;
